/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ReactElement } from 'react'
import { createElement } from 'react'

import { extractContentIdFromUrl, shouldAddTracking } from '../utils/get-tracking-info'
import { componentMap } from './component-map'

export const renderArticleElements = (
  articleBody: any = [],
  additionalInfo?: {
    articleUrl?: string
    subVerticalSlug?: string
    context?: string
    title?: string
    showFreeAccountLock?: boolean
    userLoggedIn?: boolean
  },
  state = { h2Found: false }
): ReactElement => {
  return articleBody.reduce((elements: any[], currentElement: any) => {
    if (!currentElement?.contentTag && !currentElement?.tagName && !currentElement?.component) {
      if (!skipableElements.includes(currentElement?.name ?? '')) {
        console.error('ARTICLE RENDERER ERROR: Invalid article body json element:', {
          articleUrl: additionalInfo?.articleUrl ?? 'unknown',
          invalidElement: currentElement,
        })
      }

      return elements
    }

    if (currentElement.contentTag) {
      return elements.concat(renderArticleElements(currentElement.children, additionalInfo, state))
    }

    if (currentElement.tagName === 'text') {
      return elements.concat(currentElement.innerText)
    }

    let renderedElement: ReactElement

    if (currentElement.component) {
      const component = componentMap[currentElement.component as keyof typeof componentMap]
      const componentRequiresRenderer = componentsWithRenderers.includes(currentElement.component)

      const combinedInnerText = currentElement.children
        ? currentElement.children.map((child: any) => child.innerText).join('')
        : ''

      const rendererProp = componentRequiresRenderer
        ? {
            renderArticleElements: (body: any) =>
              renderArticleElements(body, additionalInfo, state),
          }
        : {}

      if (
        currentElement.component === 'StandardLink' &&
        shouldAddTracking(additionalInfo?.subVerticalSlug) &&
        additionalInfo?.context === 'accordion' &&
        additionalInfo?.articleUrl?.includes(`/topic/`)
      ) {
        const trackingProps = {
          'data-which-id': `${additionalInfo.subVerticalSlug} link`,
          'data-section': additionalInfo.title,
          'data-card-name': combinedInnerText,
          'data-content-id': extractContentIdFromUrl(currentElement.props.href),
        }

        renderedElement = createElement(
          (component as any) || 'a',
          component
            ? {
                key: currentElement.props?.key || currentElement.id,
                ...currentElement.props,
                ...trackingProps,
                ...rendererProp,
              }
            : { key: currentElement.props?.key || currentElement.id },
          calculateElementContent({
            element: currentElement,
            additionalInfo,
            state,
          })
        )
      } else {
        renderedElement = createElement(
          (component as any) || 'div',
          component
            ? {
                key: currentElement.props?.key || currentElement.id,
                ...currentElement.props,
                ...rendererProp,
              }
            : { key: currentElement.props?.key || currentElement.id },
          calculateElementContent({
            element: currentElement,
            additionalInfo,
            state,
          })
        )
      }
    } else {
      renderedElement = createElement(
        currentElement.tagName,
        { ...currentElement.props, key: currentElement.props?.key || currentElement.id },
        calculateElementContent({
          element: currentElement,
          additionalInfo,
          state,
        })
      )
    }

    const updatedElements = elements.concat(renderedElement)

    if (
      additionalInfo?.showFreeAccountLock &&
      !state.h2Found &&
      currentElement.tagName?.toLowerCase() === 'h2'
    ) {
      state.h2Found = true
      const LockComponent = componentMap['FreeArticleSignup' as keyof typeof componentMap]

      if (LockComponent) {
        updatedElements.push(
          createElement(
            'div',
            {
              key: 'free-article-signup-wrapper',
              className: 'free-article-signup-anchor',
            },
            createElement(LockComponent as any, {
              loggedIn: additionalInfo?.userLoggedIn,
            })
          )
        )
      }
    }

    return updatedElements
  }, [])
}

const calculateElementContent = ({
  element,
  additionalInfo,
  state,
}: {
  element: any
  additionalInfo?: {
    articleUrl?: string
    subVerticalSlug?: string
    context?: string
    title?: string
    showFreeAccountLock?: boolean
    userLoggedIn?: boolean
  }
  state: { h2Found: boolean }
}): string | ReactElement | null => {
  const { innerText, children } = element

  if (innerText) {
    return innerText
  }

  if (children) {
    return renderArticleElements(children, additionalInfo, state)
  }

  return null
}

const componentsWithRenderers = ['CsvTable']

const skipableElements = ['logged-in-content', 'logged-out-content']
